<template>
  <div class="home">
    <!-- Hero Section with Code Animation -->
    <section class="hero">
      <div class="code-particles"></div>
      <div class="hero__content">
        <div class="hero__headline">
          <div class="code-bracket">{</div>
          <h1 class="hero__title">
            Mastering DSA with <span class="clip clip_codeitup">CodeitUp</span>
          </h1>
          <div class="code-bracket">}</div>
        </div>
        <p class="hero__subtitle">
          Let's have a pro coding journey with us.
        </p>
        <div class="hero__stats">
          <div class="stat-item">
            <div class="stat-number">500+</div>
            <div class="stat-label">Problems</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">10+</div>
            <div class="stat-label">Languages</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">50K+</div>
            <div class="stat-label">Submissions</div>
          </div>
        </div>
        <div class="hero__buttons">
          <vs-button
              :to="{ name: 'problem' }"
              color="primary"
              size="large"
              class="hero__button primary-btn">
            <i class='bx bx-code-alt'></i> Start Coding
          </vs-button>
          <vs-button
              :to="{ name: 'contest' }"
              color="primary" 
              type="border"
              size="large"
              class="hero__button">
            <i class='bx bx-trophy'></i> Join Contests
          </vs-button>
        </div>
      </div>
    </section>


    <!-- Features Section -->
    <section class="features">
      <h2 class="section-title">Why CodeitUp is Different</h2>
      <div class="features__grid">
        <div class="feature-card">
          <div class="feature-card__icon material">
            <span class="material-icons">terminal</span>
          </div>
          <h3 class="feature-card__title">Interactive Coding Environment</h3>
          <p class="feature-card__description">
            Write, compile and test your code in our powerful browser-based IDE with syntax highlighting and intelligent code completion.
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-card__icon material">
            <span class="material-icons">school</span>
          </div>
          <h3 class="feature-card__title">Structured Learning Path</h3>
          <p class="feature-card__description">
            Follow our carefully designed curriculum to master fundamental to advanced Data Structures & Algorithms concepts.
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-card__icon material">
            <span class="material-icons">timer</span>
          </div>
          <h3 class="feature-card__title">Timed Contests</h3>
          <p class="feature-card__description">
            Test your skills under pressure with our regular coding contests and compete with peers on our real-time leaderboard.
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-card__icon material">
            <span class="material-icons">bar_chart</span>
          </div>
          <h3 class="feature-card__title">Performance Analytics</h3>
          <p class="feature-card__description">
            Track your progress with detailed statistics on problem-solving patterns, completion rates, and areas for improvement.
          </p>
        </div>
      </div>
    </section>

    <!-- Code Snippet Section -->
    <section class="code-snippet">
      <h2 class="section-title">Learn By Doing</h2>
      <div class="code-learning-container">
        <div class="code-explanation">
          <div class="code-badge">Popular Problem</div>
          <h3 class="code-title">Two Sum</h3>
          <p class="code-description">
            Given an array of integers and a target sum, return the indices of two numbers that add up to the target.
            This problem teaches you hash maps and efficient lookup - a common interview question.
          </p>
          <div class="code-stats">
            <div class="stat">
              <span class="material-icons">emoji_events</span>
              <span>Top 5 Interview Question</span>
            </div>
            <div class="stat">
              <span class="material-icons">code</span>
              <span>Multiple Language Support</span>
            </div>
          </div>
        </div>
        <div class="snippet-container">
          <div class="snippet-header">
            <div class="snippet-tabs">
              <div class="tab" :class="{ active: activeTab === 'java' }" @click="setActiveTab('java')">TwoSum.java</div>
              <div class="tab" :class="{ active: activeTab === 'python' }" @click="setActiveTab('python')">TwoSum.py</div>
              <div class="tab" :class="{ active: activeTab === 'javascript' }" @click="setActiveTab('javascript')">TwoSum.js</div>
            </div>
            <div class="snippet-actions">
              <button class="action-button"><span class="material-icons">play_arrow</span></button>
              <button class="action-button" @click="copyCode"><span class="material-icons">content_copy</span></button>
            </div>
          </div>
          <div class="snippet-code">
            <pre v-if="activeTab === 'java'"><code v-html="javaCodeHighlighted"></code></pre>
            <pre v-if="activeTab === 'python'"><code v-html="pythonCodeHighlighted"></code></pre>
            <pre v-if="activeTab === 'javascript'"><code v-html="jsCodeHighlighted"></code></pre>
          </div>
          <div class="snippet-footer">
            <vs-button size="small" class="try-button" :to="{ name: 'problem' }">
              <span class="material-icons">code</span> Try It Yourself
            </vs-button>
            <vs-button size="small" type="border" class="browse-button" :to="{ name: 'problem' }">
              <span class="material-icons">list</span> Browse Problems
            </vs-button>
          </div>
        </div>
      </div>
    </section>

    <!-- Getting Started Section -->
    <section class="getting-started">
      <h2 class="section-title">Your Journey Starts Here</h2>
      <div class="getting-started__steps">
        <div class="step">
          <div class="step__number">01</div>
          <h3 class="step__title">Create Account</h3>
          <p class="step__description">Sign up for free and get immediate access to hundreds of problems.</p>
        </div>
        <div class="step">
          <div class="step__number">02</div>
          <h3 class="step__title">Start with Basics</h3>
          <p class="step__description">Begin with easy problems and gradually build up your skills.</p>
        </div>
        <div class="step">
          <div class="step__number">03</div>
          <h3 class="step__title">Join the Community</h3>
          <p class="step__description">Discuss solutions, share insights, and learn from peers.</p>
        </div>
      </div>
      <vs-button
          v-if="!authStore.isLoggedIn"
          :to="{ name: 'auth' }"
          color="primary"
          size="large"
          class="getting-started__cta">
        <i class='bx bx-rocket'></i> Create Free Account
      </vs-button>
    </section>

    <!-- Testimonial -->
    <section class="testimonial">
      <div class="testimonial__content">
        <div class="quote-icon"><i class='bx bxs-quote-left'></i></div>
        <p class="testimonial__text">CodeitUp transformed my approach to problem-solving. After three months of consistent practice, I aced my technical interviews and landed my dream job!</p>
        <div class="testimonial__author">
          <div class="author-name">- Bijit M.</div>
          <div class="author-title">Software Engineer at Adobe</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useAuthStore } from '@/lib/store/auth.store';
import { useHead } from '@unhead/vue';
import { onMounted, ref } from 'vue';
import hljs from 'highlight.js/lib/core';
import java from 'highlight.js/lib/languages/java';
import python from 'highlight.js/lib/languages/python';
import javascript from 'highlight.js/lib/languages/javascript';
import 'highlight.js/styles/atom-one-dark.css';

// Register the languages we need
hljs.registerLanguage('java', java);
hljs.registerLanguage('python', python);
hljs.registerLanguage('javascript', javascript);

// Meta information
useHead({
  title: 'CodeitUp: Your Ultimate DSA Practice Platform',
  meta: [
    { name: 'description', content: 'Level up your coding skills with CodeitUp. Practice DSA problems, participate in contests, and become a programming champion.' },
    { name: 'keywords', content: 'DSA, coding, programming, contests, problem-solving, CodeitUp, competitive programming, algorithms, data structures' },
    { property: 'og:title', content: 'CodeitUp: Your Ultimate DSA Practice Platform' },
    { property: 'og:description', content: 'Practice coding problems, participate in contests, and improve your problem-solving skills.' },
    { property: 'og:image', content: 'https://bijit.xyz/cockpit-master/storage/uploads/2025/01/05/logo_uid_677abba1dcae1.png' },
    { property: 'og:url', content: 'https://codeitup.bijit.xyz/app' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'CodeitUp: Your Ultimate DSA Practice Platform' },
    { name: 'twitter:description', content: 'Level up your coding skills with CodeitUp. Practice DSA problems, participate in contests, and become a programming champion.' },
    { name: 'twitter:image', content: 'https://bijit.xyz/cockpit-master/storage/uploads/2025/01/05/logo_uid_677abba1dcae1.png' },
  ],
});

const authStore = useAuthStore();
const activeTab = ref('java');

// Sample code in different languages
const javaCode = `public int[] twoSum(int[] nums, int target) {
    // Create a hash map to store values and their indices
    Map<Integer, Integer> map = new HashMap<>();
    
    // Iterate through the array
    for (int i = 0; i < nums.length; i++) {
        // Calculate the complement
        int complement = target - nums[i];
        
        // Check if the complement exists in the map
        if (map.containsKey(complement)) {
            // Return the indices of the two numbers
            return new int[] { map.get(complement), i };
        }
        
        // Add the current number to the map
        map.put(nums[i], i);
    }
    
    // No solution found
    throw new IllegalArgumentException("No two sum solution");
}`;

const pythonCode = `def two_sum(nums, target):
    # Create a dictionary to store values and their indices
    num_map = {}
    
    # Iterate through the array
    for i, num in enumerate(nums):
        # Calculate the complement
        complement = target - num
        
        # Check if the complement exists in the map
        if complement in num_map:
            # Return the indices of the two numbers
            return [num_map[complement], i]
        
        # Add the current number to the map
        num_map[num] = i
    
    # No solution found
    raise ValueError("No two sum solution")`;

const jsCode = `function twoSum(nums, target) {
    // Create a map to store values and their indices
    const map = new Map();
    
    // Iterate through the array
    for (let i = 0; i < nums.length; i++) {
        // Calculate the complement
        const complement = target - nums[i];
        
        // Check if the complement exists in the map
        if (map.has(complement)) {
            // Return the indices of the two numbers
            return [map.get(complement), i];
        }
        
        // Add the current number to the map
        map.set(nums[i], i);
    }
    
    // No solution found
    throw new Error("No two sum solution");
}`;

// Highlight the code
const javaCodeHighlighted = hljs.highlight(javaCode, { language: 'java' }).value;
const pythonCodeHighlighted = hljs.highlight(pythonCode, { language: 'python' }).value;
const jsCodeHighlighted = hljs.highlight(jsCode, { language: 'javascript' }).value;

// Set active tab
const setActiveTab = (tab) => {
  activeTab.value = tab;
};

// Copy code to clipboard
const copyCode = () => {
  let codeToCopy = '';
  
  switch (activeTab.value) {
    case 'java':
      codeToCopy = javaCode;
      break;
    case 'python':
      codeToCopy = pythonCode;
      break;
    case 'javascript':
      codeToCopy = jsCode;
      break;
  }
  
  navigator.clipboard.writeText(codeToCopy)
    .then(() => {
      // Could add a notification here if desired
      console.log('Code copied to clipboard');
    })
    .catch(err => {
      console.error('Could not copy text: ', err);
    });
};

onMounted(() => {
  // Animation for code particles could be initialized here if needed
});
</script>

<style scoped>
.home {
  padding: 2rem 2rem 4rem;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
  overflow-x: hidden;
}

/* Hero Section */
.hero {
  position: relative;
  padding: 6rem 0;
  margin-bottom: 4rem;
  overflow: hidden;
  border-radius: 1.5rem;
  background: linear-gradient(135deg, var(--background-light) 0%, var(--background) 100%);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.code-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(var(--primary-transparent) 1px, transparent 1px),
    radial-gradient(var(--primary-transparent) 1px, transparent 1px);
  background-size: 20px 20px;
  background-position: 0 0, 10px 10px;
  opacity: 0.3;
  z-index: 0;
}

.hero__content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 0 2rem;
}

.hero__headline {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
}

.code-bracket {
  font-size: 5rem;
  font-weight: 700;
  color: var(--primary);
  margin: 0 1rem;
  font-family: 'Fira Code', monospace;
  opacity: 0.8;
}

.hero__title {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.typing-effect {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  border-right: 0.15em solid var(--primary);
  animation: typing 3s steps(40, end), blink-caret 0.75s step-end infinite;
}

@keyframes typing {
  from { width: 0 }
  to { width: 100% }
}

@keyframes blink-caret {
  from, to { border-color: transparent }
  50% { border-color: var(--primary) }
}

.hero__subtitle {
  font-size: 1.25rem;
  color: var(--text-secondary);
  margin: 0 auto 2.5rem;
  max-width: 700px;
  line-height: 1.7;
}

.hero__stats {
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;
  gap: 3rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.hero__buttons {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
}

.hero__button {
  padding: 0.75rem 1.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.hero__button i {
  margin-right: 0.5rem;
  font-size: 1.25rem;
  vertical-align: middle;
}

/* Categories Section */
.categories {
  margin-bottom: 5rem;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 3rem;
  position: relative;
  padding-bottom: 1rem;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: var(--primary);
  border-radius: 2px;
}

.categories__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 2rem;
}

.category-card {
  background: var(--background-light);
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid var(--border-color);
}

.category-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.category-icon {
  font-size: 2.5rem;
  color: var(--primary);
  margin-bottom: 1rem;
}

.category-card h3 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.difficulty-tags {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.tag {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.easy {
  background: rgba(46, 213, 115, 0.2);
  color: #2ed573;
}

.medium {
  background: rgba(255, 165, 2, 0.2);
  color: #ffa502;
}

.hard {
  background: rgba(255, 71, 87, 0.2);
  color: #ff4757;
}

/* Features Section */
.features {
  margin-bottom: 5rem;
}

.features__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: var(--background-light);
  padding: 2.5rem;
  border-radius: 1rem;
  text-align: left;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: var(--primary);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-card__icon.material {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--primary-transparent) 0%, rgba(79, 70, 229, 0.4) 100%);
  box-shadow: 0 10px 20px rgba(79, 70, 229, 0.15);
}

.feature-card__icon.material .material-icons {
  font-size: 2.25rem;
  color: var(--primary);
}

.feature-card__title {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.feature-card__description {
  color: var(--text-secondary);
  line-height: 1.7;
}

/* Code Snippet Section */
.code-snippet {
  margin-bottom: 5rem;
}

.code-learning-container {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.code-explanation {
  flex: 1;
  min-width: 300px;
  background: var(--background-light);
  border-radius: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid var(--border-color);
}

.code-badge {
  background: var(--primary);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 1rem;
  width: fit-content;
}

.code-title {
  font-size: 2rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.code-description {
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 2rem;
}

.code-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stat .material-icons {
  color: var(--primary);
  font-size: 1.25rem;
}

.snippet-container {
  flex: 1.5;
  min-width: 450px;
  background: var(--dark);
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.snippet-header {
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.snippet-tabs {
  display: flex;
  gap: 1rem;
}

.snippet-actions {
  display: flex;
  gap: 0.5rem;
}

.action-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  border-radius: 4px;
  padding: 0.25rem;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.action-button .material-icons {
  font-size: 1.25rem;
}

.tab {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.tab.active {
  background: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.snippet-code {
  padding: 2rem;
  overflow-x: auto;
}

.snippet-code pre {
  margin: 0;
}

.snippet-code :deep(code) {
  font-family: 'Fira Code', monospace;
  line-height: 1.6;
  font-size: 14px;
}

.snippet-code :deep(.hljs) {
  background: transparent;
  padding: 0;
}

.snippet-footer {
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.try-button, .browse-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.try-button .material-icons, .browse-button .material-icons {
  font-size: 1.15rem;
}

/* Getting Started Section */
.getting-started {
  padding: 5rem 2rem;
  text-align: center;
  background: var(--background-light);
  border-radius: 1.5rem;
  margin-bottom: 5rem;
  position: relative;
  overflow: hidden;
}

.getting-started::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(135deg, var(--primary-transparent) 25%, transparent 25%),
                    linear-gradient(225deg, var(--primary-transparent) 25%, transparent 25%),
                    linear-gradient(45deg, var(--primary-transparent) 25%, transparent 25%),
                    linear-gradient(315deg, var(--primary-transparent) 25%, transparent 25%);
  background-position: 10px 0, 10px 0, 0 0, 0 0;
  background-size: 20px 20px;
  background-repeat: repeat;
  opacity: 0.05;
  z-index: 0;
}

.getting-started__steps {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 4rem;
  position: relative;
  z-index: 1;
}

.step {
  flex: 1;
  max-width: 300px;
}

.step__number {
  width: 60px;
  height: 60px;
  background: var(--primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 1.5rem;
  font-family: 'Fira Code', monospace;
}

.step__title {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: 700;
}

.step__description {
  color: var(--text-secondary);
  line-height: 1.6;
}

.getting-started__cta {
  position: relative;
  z-index: 1;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.getting-started__cta i {
  margin-right: 0.5rem;
  vertical-align: middle;
}

/* Testimonial Section */
.testimonial {
  margin-bottom: 3rem;
  background: var(--background-light);
  border-radius: 1.5rem;
  padding: 3rem 2rem;
  text-align: center;
}

.testimonial__content {
  max-width: 800px;
  margin: 0 auto;
}

.quote-icon {
  font-size: 3rem;
  color: var(--primary);
  opacity: 0.3;
  margin-bottom: 1rem;
}

.testimonial__text {
  font-size: 1.5rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  font-style: italic;
}

.testimonial__author {
  font-weight: 600;
}

.author-name {
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.author-title {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

/* Responsive Styles */
@media (max-width: 992px) {
  .hero__title {
    font-size: 3rem;
  }
  
  .code-bracket {
    font-size: 4rem;
  }
  
  .hero__stats {
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .home {
    padding: 1rem 1rem 3rem;
  }
  
  .hero {
    padding: 4rem 0;
    margin-bottom: 2rem;
    border-radius: 1rem;
  }
  
  .hero__headline {
    flex-direction: column;
  }
  
  .code-bracket {
    font-size: 3rem;
    margin: 0.5rem 0;
  }
  
  .hero__title {
    font-size: 2.25rem;
    padding: 0 1rem;
  }
  
  .hero__stats {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .hero__subtitle {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
  
  .hero__buttons {
    flex-direction: column;
    gap: 1rem;
    padding: 0 1rem;
  }

  .hero__button {
    width: 100%;
  }
  
  .section-title {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  
  .features__grid {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }

  .feature-card {
    padding: 1.5rem;
  }

  .code-learning-container {
    flex-direction: column;
  }

  .code-explanation {
    min-width: auto;
  }

  .snippet-container {
    min-width: auto;
    width: 100%;
  }

  .snippet-code {
    padding: 1rem;
  }

  .snippet-code :deep(code) {
    font-size: 12px;
  }

  .getting-started__steps {
    flex-direction: column;
    align-items: center;
    gap: 2.5rem;
    padding: 0 1rem;
  }

  .step {
    max-width: 100%;
  }
  
  .testimonial {
    padding: 2rem 1rem;
  }

  .testimonial__text {
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .hero__title {
    font-size: 1.75rem;
  }

  .code-bracket {
    font-size: 2.5rem;
  }

  .hero__subtitle {
    font-size: 1rem;
  }

  .stat-number {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .feature-card__title {
    font-size: 1.25rem;
  }

  .code-title {
    font-size: 1.5rem;
  }

  .snippet-tabs {
    flex-wrap: wrap;
  }

  .tab {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }

  .snippet-code :deep(code) {
    font-size: 11px;
  }

  .testimonial__text {
    font-size: 1.1rem;
  }
}

@media (max-width: 320px) {
  .hero__title {
    font-size: 1.5rem;
  }

  .code-bracket {
    font-size: 2rem;
  }
}

/* CSS Variables - defined in your global CSS or can be added here */
:root {
  --primary-transparent: rgba(79, 70, 229, 0.2);
  --background-light: #1e1e2d;
  --background-lighter: #2a2a3d;
  --border-color: rgba(255, 255, 255, 0.1);
  --text-secondary: rgba(255, 255, 255, 0.7);
}
</style>
